if (!customElements.get('lm-pdf-drawer')) {
  class LmPdfDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.openButton = this.querySelector('[data-pdf-open]');
      this.overlay = this.querySelector('[data-pdf-overlay]');
      this.panel = this.querySelector('[data-pdf-panel]');
      this.closeButton = this.querySelector('[data-pdf-close]');
      this.frame = this.querySelector('[data-pdf-frame]');
      this.canvas = this.querySelector('[data-pdf-canvas]');
      this.imagePages = Array.from(this.querySelectorAll('[data-pdf-page-image]'));
      this.mobileViewer = this.querySelector('[data-pdf-mobile]');
      this.status = this.querySelector('[data-pdf-status]');
      this.pageCurrent = this.querySelector('[data-pdf-current]');
      this.pageTotal = this.querySelector('[data-pdf-total]');
      this.prevButton = this.querySelector('[data-pdf-prev]');
      this.nextButton = this.querySelector('[data-pdf-next]');
      this.zoomOutButton = this.querySelector('[data-pdf-zoom-out]');
      this.zoomInButton = this.querySelector('[data-pdf-zoom-in]');
      this.pageNumber = 1;
      this.zoom = 1;
      if (!this.openButton || !this.overlay || !this.panel || !this.closeButton || !this.frame) return;
      this.openButton.addEventListener('click', () => this.open());
      this.closeButton.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());
      this.prevButton?.addEventListener('click', () => this.changePage(-1));
      this.nextButton?.addEventListener('click', () => this.changePage(1));
      this.zoomOutButton?.addEventListener('click', () => this.changeZoom(-0.15));
      this.zoomInButton?.addEventListener('click', () => this.changeZoom(0.15));
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.panel.classList.contains('is-open')) this.close();
      });
    }
    async open() {
      this.overlay.classList.add('is-open');
      this.panel.classList.add('is-open');
      document.body.classList.add('pdd-no-scroll');
      this.closeButton.focus();
      if (matchMedia('(max-width: 749px)').matches) await this.loadMobileViewer();
      else if (!this.frame.src) this.frame.src = this.dataset.pdfUrl;
    }
    close() {
      this.overlay.classList.remove('is-open');
      this.panel.classList.remove('is-open');
      document.body.classList.remove('pdd-no-scroll');
      this.openButton.focus();
    }
    async loadMobileViewer() {
      if (this.imagePages.length) {
        this.pdfDocument = { numPages: this.imagePages.length, imageMode: true };
        if (this.canvas) {
          this.canvas.hidden = true;
          this.canvas.style.display = 'none';
        }
        this.pageTotal.textContent = this.imagePages.length;
        this.status.textContent = '';
        await this.renderPage();
        return;
      }
      if (this.pdfDocument) return;
      this.status.textContent = 'Chargement du guide…';
      try {
        const pdfjs = await import(this.dataset.pdfjsUrl);
        pdfjs.GlobalWorkerOptions.workerSrc = this.dataset.pdfWorkerUrl;
        this.pdfDocument = await pdfjs.getDocument({
          url: this.dataset.pdfUrl,
          isOffscreenCanvasSupported: false,
          isImageDecoderSupported: false
        }).promise;
        this.pageTotal.textContent = this.pdfDocument.numPages;
        this.status.textContent = '';
        await this.renderPage();
      } catch (error) {
        this.status.textContent = "Le lecteur n'a pas pu charger ce document.";
      }
    }
    async renderPage() {
      if (!this.pdfDocument) return;
      if (this.pdfDocument.imageMode) {
        this.imagePages.forEach((image, index) => {
          image.classList.toggle('is-active', index === this.pageNumber - 1);
          image.style.width = `${Math.round(this.zoom * 100)}%`;
        });
        this.pageCurrent.textContent = this.pageNumber;
        this.prevButton.disabled = this.pageNumber <= 1;
        this.nextButton.disabled = this.pageNumber >= this.pdfDocument.numPages;
        return;
      }
      if (!this.canvas) return;
      this.canvas.hidden = false;
      this.canvas.style.display = 'block';
      if (this.renderTask) this.renderTask.cancel();
      const page = await this.pdfDocument.getPage(this.pageNumber);
      const base = page.getViewport({ scale: 1 });
      const available = Math.max(280, this.mobileViewer.clientWidth - 20);
      const cssScale = available / base.width * this.zoom;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: cssScale * pixelRatio });
      this.canvas.width = Math.floor(viewport.width);
      this.canvas.height = Math.floor(viewport.height);
      this.canvas.style.width = `${Math.floor(viewport.width / pixelRatio)}px`;
      this.canvas.style.height = `${Math.floor(viewport.height / pixelRatio)}px`;
      const context = this.canvas.getContext('2d', { alpha: false });
      context.save();
      context.fillStyle = '#fff';
      context.fillRect(0, 0, this.canvas.width, this.canvas.height);
      context.restore();
      this.renderTask = page.render({ canvas: this.canvas, canvasContext: context, viewport });
      try { await this.renderTask.promise; } catch (error) { if (error?.name !== 'RenderingCancelledException') throw error; }
      this.pageCurrent.textContent = this.pageNumber;
      this.prevButton.disabled = this.pageNumber <= 1;
      this.nextButton.disabled = this.pageNumber >= this.pdfDocument.numPages;
    }
    async changePage(delta) {
      if (!this.pdfDocument) return;
      this.pageNumber = Math.min(this.pdfDocument.numPages, Math.max(1, this.pageNumber + delta));
      await this.renderPage();
    }
    async changeZoom(delta) {
      this.zoom = Math.min(1.75, Math.max(0.7, this.zoom + delta));
      await this.renderPage();
    }
  }
  customElements.define('lm-pdf-drawer', LmPdfDrawer);
}
